/**
 * Copyright (c) HashiCorp, Inc.
 */

import Component from '@glimmer/component';
import {
  HdsBadge,
  HdsTooltipButton,
} from '@hashicorp/design-system-components/components';

import type { HdsTooltipSignature } from '@hashicorp/design-system-components/components/hds/tooltip-button/index';

export interface MetadataServiceHealthBadgeSignature {
  Args: {
    successCount?: number;
    criticalCount?: number;
    warningCount?: number;
  };
  Element: HdsTooltipSignature['Element'];
}

export default class MetadataServiceHealthBadge extends Component<MetadataServiceHealthBadgeSignature> {
  get hasStatus(): boolean {
    const { criticalCount, warningCount, successCount } = this.args;

    return !!criticalCount || !!warningCount || !!successCount;
  }

  get statusIsCritical(): boolean {
    const { criticalCount } = this.args;
    return !!criticalCount && criticalCount > 0;
  }

  get statusIsWarning(): boolean {
    const { warningCount } = this.args;
    return !!warningCount && warningCount > 0 && !this.statusIsCritical;
  }

  <template>
    {{#if this.hasStatus}}
      <HdsTooltipButton
        @text={{if
          this.statusIsCritical
          "1 or more instances is critical"
          (if
            this.statusIsWarning
            "1 or more instances has a warning"
            "All instances are healthy"
          )
        }}
        @placement="bottom-start"
      >
        {{#if this.statusIsCritical}}
          <HdsBadge
            @color="critical"
            @icon="x"
            @text="Critical"
            data-test-service-health-check-critical
          />
        {{else if this.statusIsWarning}}
          <HdsBadge
            @color="warning"
            @icon="alert-triangle"
            @text="Warning"
            data-test-service-health-check-warning
          />
        {{else}}
          <HdsBadge
            @color="success"
            @icon="check"
            @text="Healthy"
            a-test-service-health-check-healthy
          />
        {{/if}}
      </HdsTooltipButton>
    {{/if}}
  </template>
}
